import { useHasPermissionFromContext } from '@components/PermissionsContext';
import { usePhoneLib } from '@components/Phone/PhoneValue';
import { omit } from 'lodash-es';
import { FC, ReactElement, ReactNode } from 'react';
import { SetOptional } from 'type-fest';
import { Field, FormGroupProps } from '..';
import { Props as InputProps, PhoneInput } from '../../Phone/PhoneInput';
import { LockedField, Props as LockedFieldProps } from '../LockedField';

// ts-unused-exports:disable-next-line
export interface Props
  extends Omit<
      FormGroupProps<string>,
      | 'checkboxSize'
      | 'onChange'
      | 'value'
      | 'onKeyDown'
      | 'onFocus'
      | 'onBlur'
      | 'children'
    >,
    Omit<SetOptional<InputProps, 'onChange'>, 'name'>,
    Pick<LockedFieldProps, 'initialLocked' | 'lockable' | 'initialAutofocus'> {
  //
}

export const lockMask = (s: Maybe<string>): string =>
  s ? `***${s.replace(/\D/gi, '').slice(-4)}` : '';

const truthy = (): boolean => true;

export const PhoneField: FC<Props> = (rawProps) => {
  const phoneLib = usePhoneLib();
  const isValid = phoneLib ? phoneLib.isValidPhoneNumber : truthy;
  const [userCanEdit] = useHasPermissionFromContext();
  const { lockable = false, validate, initialAutofocus, ...props } = rawProps;
  return (
    <Field<string>
      {...omit(props, ['onChange', 'onBlur', 'onFocus', 'onKeyDown'])}
      validate={(val): fixMe => {
        const pass = isValid(val || '');
        if (!pass && val) {
          return 'Invalid number';
        } else if (validate) {
          return validate(val);
        }
      }}
    >
      {({ renderLabel, ...rest }): ReactElement => {
        return (
          <div>
            {renderLabel(rest.field)}
            <LockedField
              onLockChange={(): void => {
                rest.form.setFieldValue(props.name, null);
              }}
              lockedInputProps={{ value: lockMask(rest.field.value) }}
              lockable={lockable}
              initialAutofocus={initialAutofocus}
            >
              {({ fieldProps }): ReactNode => (
                <PhoneInput
                  readOnly={!userCanEdit}
                  {...props}
                  {...rest.field}
                  hideCountryPicker={props.hideCountryPicker || lockable}
                  // For some reason rest.field.onChange isn't working...
                  // Using rest.form.setFieldValue instead
                  onChange={(e): void => {
                    if (!props.onChange) {
                      return rest.form.setFieldValue(props.name, e || '');
                    }
                    props.onChange(e);
                  }}
                  id={rest.htmlFor}
                  {...fieldProps({
                    onBlur: (e): void => {
                      if (!props.onBlur) {
                        return rest.form.setFieldTouched(props.name, true);
                      }
                      props.onBlur(e);
                    },
                  })}
                />
              )}
            </LockedField>
          </div>
        );
      }}
    </Field>
  );
};
